import { useMemo, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { apiRequest, queryClient } from '@/lib/queryClient';
import type { Customer, PipelineStage } from '../types';

export interface CustomerUpdateData {
  id: number;
  updates: Record<string, unknown>;
}

/**
 * Shape of pipeline data from /api/pipeline-stages/data endpoint
 */
interface PipelineData {
  stages: PipelineStage[];
  customers: Customer[];
  stageCustomerCounts: Record<number, number>;
  stageTotalValues: Record<number, number>;
}

export interface UsePipelineMutationsReturn {
  updateCustomerMutation: ReturnType<typeof useMutation<unknown, Error, CustomerUpdateData>>;
  deleteCustomerMutation: ReturnType<typeof useMutation<unknown, Error, number>>;
  createOpenSolarProjectMutation: ReturnType<typeof useMutation<unknown, Error, number>>;
  updateLeadScore: (customerId: number, score: number) => void;
}

/**
 * Hook that provides all pipeline-related mutations with optimistic updates
 * Handles customer updates, deletions, and OpenSolar project creation
 *
 * Optimistic updates make UI feel instant by updating cache immediately,
 * then rolling back on error. This eliminates perceived 200-500ms lag.
 */
export function usePipelineMutations(): UsePipelineMutationsReturn {
  const { toast } = useToast();

  const updateCustomerMutation = useMutation({
    mutationFn: (data: CustomerUpdateData) => {
      return apiRequest('PATCH', `/api/customers/${data.id}`, data.updates);
    },
    // Optimistic update: immediately update cache before server responds
    onMutate: async (data: CustomerUpdateData) => {
      // Cancel any outgoing refetches to prevent race conditions
      await queryClient.cancelQueries({ queryKey: ['/api/pipeline-stages/data'] });

      // Snapshot the previous value for rollback
      const previousData = queryClient.getQueryData<PipelineData>(['/api/pipeline-stages/data']);

      // Optimistically update the cache
      queryClient.setQueryData<PipelineData>(['/api/pipeline-stages/data'], (old) => {
        if (!old) return old;

        // Filter out undefined values to prevent overwriting existing data
        const filteredUpdates = Object.fromEntries(
          Object.entries(data.updates).filter(([_, v]) => v !== undefined)
        );

        // Find the customer being updated
        const oldCustomer = old.customers.find(c => c.id === data.id);
        const oldStageId = oldCustomer?.pipelineStageId;
        const newStageId = data.updates.pipelineStageId as number | undefined;

        // Update customers array using filtered updates
        // Delete pre-computed cache fields so they rebuild on next filter/search pass
        const updatedCustomers = old.customers.map((customer) => {
          if (customer.id !== data.id) return customer;
          const merged = { ...customer, ...filteredUpdates, updatedAt: new Date().toISOString() };
          delete merged._searchHaystack;
          delete merged.createdAtTimestamp;
          delete merged.lastContactTimestamp;
          delete merged.pipelineUpdatedAtTimestamp;
          delete merged.nextContactTimestamp;
          return merged;
        });

        // Recalculate stage counts if stage changed
        let updatedCounts = { ...old.stageCustomerCounts };
        let updatedValues = { ...old.stageTotalValues };

        if (newStageId !== undefined && oldStageId !== newStageId) {
          // Decrement old stage count
          if (oldStageId && updatedCounts[oldStageId]) {
            updatedCounts[oldStageId] = Math.max(0, updatedCounts[oldStageId] - 1);
          }
          // Increment new stage count
          updatedCounts[newStageId] = (updatedCounts[newStageId] || 0) + 1;

          // Update total values if customer has a deal value
          const dealValue = oldCustomer?.dealValue || 0;
          if (oldStageId && updatedValues[oldStageId]) {
            updatedValues[oldStageId] = Math.max(0, updatedValues[oldStageId] - dealValue);
          }
          updatedValues[newStageId] = (updatedValues[newStageId] || 0) + dealValue;
        }

        return {
          ...old,
          customers: updatedCustomers,
          stageCustomerCounts: updatedCounts,
          stageTotalValues: updatedValues,
        };
      });

      // Return context with snapshot for rollback
      return { previousData };
    },
    // On error, rollback to the previous state
    onError: (error: Error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['/api/pipeline-stages/data'], context.previousData);
      }
      toast({
        title: 'Error',
        description: `Failed to update customer: ${error.message}`,
        variant: 'destructive',
      });
    },
    // After success or error, refetch to ensure consistency
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/pipeline-stages/data'] });
    },
  });

  const deleteCustomerMutation = useMutation({
    mutationFn: (customerId: number) => {
      return apiRequest('DELETE', `/api/customers/${customerId}`);
    },
    // Optimistic update for delete
    onMutate: async (customerId: number) => {
      await queryClient.cancelQueries({ queryKey: ['/api/pipeline-stages/data'] });

      const previousData = queryClient.getQueryData<PipelineData>(['/api/pipeline-stages/data']);

      queryClient.setQueryData<PipelineData>(['/api/pipeline-stages/data'], (old) => {
        if (!old) return old;

        const deletedCustomer = old.customers.find(c => c.id === customerId);
        const stageId = deletedCustomer?.pipelineStageId;

        // Remove customer from array
        const updatedCustomers = old.customers.filter(c => c.id !== customerId);

        // Update stage counts
        let updatedCounts = { ...old.stageCustomerCounts };
        let updatedValues = { ...old.stageTotalValues };

        if (stageId && updatedCounts[stageId]) {
          updatedCounts[stageId] = Math.max(0, updatedCounts[stageId] - 1);
          const dealValue = deletedCustomer?.dealValue || 0;
          updatedValues[stageId] = Math.max(0, (updatedValues[stageId] || 0) - dealValue);
        }

        return {
          ...old,
          customers: updatedCustomers,
          stageCustomerCounts: updatedCounts,
          stageTotalValues: updatedValues,
        };
      });

      return { previousData };
    },
    onError: (error: Error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['/api/pipeline-stages/data'], context.previousData);
      }
      toast({
        title: 'Error',
        description: `Failed to delete customer: ${error.message}`,
        variant: 'destructive',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/pipeline-stages/data'] });
    },
  });

  const createOpenSolarProjectMutation = useMutation({
    mutationFn: (customerId: number) => {
      return apiRequest('POST', '/api/opensolar/projects/from-customer', {
        customerId,
      });
    },
    onSuccess: (data: any) => {
      const projectId = data?.project?.projectId;
      const projectUrl = data?.project?.openUrl;
      const fallbackUrl = `https://app.opensolar.com/#/projects/${projectId}/manage`;
      const urlToOpen = projectUrl || fallbackUrl;
      window.open(urlToOpen, '_blank');
    },
    onError: (error: Error) => {
      toast({
        title: 'OpenSolar Error',
        description: `Failed to open project: ${error.message || 'Unknown error'}. Please check your OpenSolar credentials.`,
        variant: 'destructive',
      });
    },
  });

  const updateLeadScore = useCallback(
    (customerId: number, score: number) => {
      updateCustomerMutation.mutate({
        id: customerId,
        updates: { leadScore: score },
      });
    },
    [updateCustomerMutation.mutate]
  );

  return useMemo(
    () => ({
      updateCustomerMutation,
      deleteCustomerMutation,
      createOpenSolarProjectMutation,
      updateLeadScore,
    }),
    [
      updateCustomerMutation,
      deleteCustomerMutation,
      createOpenSolarProjectMutation,
      updateLeadScore,
    ]
  );
}
